import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  makeStyles,
  CircularProgress,
  IconButton,
} from "@material-ui/core";
import { PhotoCamera, Send, Delete, Videocam, Audiotrack } from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorPicker from "../../components/ColorPicker";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 800,
    margin: "0 auto",
  },
  preview: {
    width: "100%",
    maxWidth: 400,
    height: "auto",
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  textPreview: {
    width: "100%",
    maxWidth: 400,
    height: 600,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  textContent: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: "#FFFFFF",
    fontSize: "24px",
    fontWeight: "bold",
    wordWrap: "break-word",
    maxWidth: "90%",
  },
  colorButton: {
    width: 50,
    height: 50,
    borderRadius: "50%",
    border: "2px solid #ccc",
    cursor: "pointer",
    margin: theme.spacing(0.5),
  },
  uploadButton: {
    marginTop: theme.spacing(2),
  },
  sendButton: {
    marginTop: theme.spacing(3),
  },
}));

const StatusPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
  const [text, setText] = useState("");
  const [backgroundColor, setBackgroundColor] = useState("#000000");
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null); // 'image', 'video', 'audio'
  const [loading, setLoading] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);

  useEffect(() => {
    const fetchWhatsapps = async () => {
      try {
        const { data } = await api.get("/whatsapp/");
        const connectedWhatsapps = data.filter((w) => w.status === "CONNECTED");
        setWhatsapps(connectedWhatsapps);
        if (connectedWhatsapps.length > 0) {
          setSelectedWhatsapp(connectedWhatsapps[0].id.toString());
        }
      } catch (err) {
        toast.error("Erro ao carregar WhatsApps");
      }
    };

    fetchWhatsapps();
  }, []);

  const handleMediaChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Verificar tamanho máximo (16MB para vídeo, 10MB para outros)
      const maxSize = file.type.startsWith("video/") ? 16 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        const maxSizeMB = maxSize / (1024 * 1024);
        toast.error(`Arquivo muito grande. Tamanho máximo: ${maxSizeMB}MB`);
        return;
      }

      // Determinar tipo de mídia
      let type = null;
      if (file.type.startsWith("image/")) {
        type = "image";
      } else if (file.type.startsWith("video/")) {
        type = "video";
      } else if (file.type.startsWith("audio/")) {
        type = "audio";
      } else {
        toast.error("Tipo de arquivo não suportado. Use imagem, vídeo ou áudio.");
        return;
      }

      setSelectedMedia(file);
      setMediaType(type);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveMedia = () => {
    setSelectedMedia(null);
    setMediaPreview(null);
    setMediaType(null);
  };

  const handleSendStatus = async () => {
    if (!selectedWhatsapp) {
      toast.error("Selecione um WhatsApp");
      return;
    }

    if (!text && !selectedMedia) {
      toast.error("Digite um texto ou selecione uma mídia (imagem/vídeo/áudio)");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("text", text);
      formData.append("backgroundColor", backgroundColor);
      formData.append("companyId", user.companyId);

      if (selectedMedia) {
        formData.append("media", selectedMedia);
      }

      await api.post(`/whatsapp/${selectedWhatsapp}/status`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Status enviado com sucesso!");
      
      // Limpar formulário
      setText("");
      setSelectedMedia(null);
      setMediaPreview(null);
      setMediaType(null);
      setBackgroundColor("#000000");
    } catch (err) {
      toast.error(err.response?.data?.error || "Erro ao enviar status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper className={classes.root}>
      <Typography variant="h4" gutterBottom>
        Postar Status
      </Typography>

      <Box mt={3}>
        <TextField
          select
          fullWidth
          label="Selecione o WhatsApp"
          value={selectedWhatsapp}
          onChange={(e) => setSelectedWhatsapp(e.target.value)}
          SelectProps={{
            native: true,
          }}
          variant="outlined"
          margin="normal"
        >
          <option value="">Selecione...</option>
          {whatsapps.map((whatsapp) => (
            <option key={whatsapp.id} value={whatsapp.id}>
              {whatsapp.name}
            </option>
          ))}
        </TextField>
      </Box>

      <Box mt={3}>
        <Typography variant="h6" gutterBottom>
          {selectedMedia ? `${mediaType === "image" ? "Imagem" : mediaType === "video" ? "Vídeo" : "Áudio"} Selecionado` : "Enviar Mídia"}
        </Typography>
        <input
          accept="image/*,video/*,audio/*"
          style={{ display: "none" }}
          id="media-upload"
          type="file"
          onChange={handleMediaChange}
        />
        <label htmlFor="media-upload">
          <Button
            variant="outlined"
            component="span"
            startIcon={
              mediaType === "image" ? <PhotoCamera /> :
              mediaType === "video" ? <Videocam /> :
              mediaType === "audio" ? <Audiotrack /> :
              <PhotoCamera />
            }
            className={classes.uploadButton}
          >
            {selectedMedia 
              ? `Trocar ${mediaType === "image" ? "Imagem" : mediaType === "video" ? "Vídeo" : "Áudio"}`
              : "Selecionar Mídia (Imagem/Vídeo/Áudio)"}
          </Button>
        </label>

        {mediaPreview && (
          <Box mt={2} position="relative" display="inline-block">
            {mediaType === "image" && (
              <img
                src={mediaPreview}
                alt="Preview"
                className={classes.preview}
              />
            )}
            {mediaType === "video" && (
              <video
                src={mediaPreview}
                controls
                className={classes.preview}
                style={{ maxHeight: 400 }}
              />
            )}
            {mediaType === "audio" && (
              <Box
                className={classes.preview}
                style={{
                  padding: 16,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 8,
                  minHeight: 100,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <audio src={mediaPreview} controls style={{ width: "100%" }} />
              </Box>
            )}
            <IconButton
              onClick={handleRemoveMedia}
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                backgroundColor: "rgba(255, 255, 255, 0.8)",
              }}
            >
              <Delete />
            </IconButton>
          </Box>
        )}
      </Box>

      <Box mt={4}>
        <Typography variant="h6" gutterBottom>
          {selectedMedia 
            ? mediaType === "audio" 
              ? "Nota: Áudio não suporta legenda" 
              : `Legenda da ${mediaType === "image" ? "Imagem" : "Vídeo"}`
            : "Enviar Texto com Cor de Fundo"}
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          label={
            selectedMedia
              ? mediaType === "audio"
                ? "Áudio não suporta legenda"
                : `Digite a legenda da ${mediaType === "image" ? "imagem" : "vídeo"} (opcional)`
              : "Digite o texto do status"
          }
          value={text}
          onChange={(e) => setText(e.target.value)}
          variant="outlined"
          margin="normal"
          disabled={selectedMedia && mediaType === "audio"}
          placeholder={
            selectedMedia
              ? mediaType === "audio"
                ? "Áudio não suporta legenda"
                : `Adicione uma legenda para sua ${mediaType === "image" ? "imagem" : "vídeo"}...`
              : "Digite o texto do status..."
          }
        />

        {!selectedMedia && (
          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              Cor de Fundo:
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                className={classes.colorButton}
                style={{ backgroundColor }}
                onClick={() => setColorPickerOpen(true)}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => setColorPickerOpen(true)}
              >
                Escolher Cor
              </Button>
            </Box>

            {text && (
              <Box
                className={classes.textPreview}
                style={{ backgroundColor }}
                mt={2}
              >
                <Typography className={classes.textContent}>{text}</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Box mt={3} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          startIcon={loading ? <CircularProgress size={20} /> : <Send />}
          onClick={handleSendStatus}
          disabled={loading || (!text && !selectedMedia)}
          className={classes.sendButton}
        >
          {loading ? "Enviando..." : "Enviar Status"}
        </Button>
      </Box>

      <ColorPicker
        open={colorPickerOpen}
        handleClose={() => setColorPickerOpen(false)}
        currentColor={backgroundColor}
        onChange={(color) => {
          setBackgroundColor(color);
        }}
      />
    </Paper>
  );
};

export default StatusPage;

